import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { memo } from "react";
import { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";
import { Carousel } from "react-responsive-carousel";

import MDBox from "@/components/atoms/MDBox";
import { getPublicImageUrl } from "@/helpers/image-url-helper";
import { IProductImage } from "@/interfaces/product-image.interface";
import { ImageData } from "@/interfaces/product.interface";

import { RemoveIconButton } from "./styles";

import "react-responsive-carousel/lib/styles/carousel.min.css";

type Props = {
  images: (ImageData | IProductImage)[];
  removeImage: (image: ImageData | IProductImage, event: React.MouseEvent) => void;
  setMainImage: (image: IProductImage) => void;
  getRootProps: <T extends DropzoneRootProps>(props?: T | undefined) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T;
};

const CarouselImages = memo(function CarouselImages({
  images,
  removeImage,
  setMainImage,
  getRootProps,
  getInputProps,
}: Props) {
  const hasNext = images.length > 3;

  const handleSetMainImage = (event: React.MouseEvent<HTMLDivElement>, image: IProductImage) => {
    event.stopPropagation();
    setMainImage(image);
  };

  return (
    <MDBox mt={10} display="flex" alignItems="center">
      {images.length < 9 && (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          bgColor="grey-100"
          borderRadius="lg"
          shadow="md"
          height="5rem"
          width="5rem"
          sx={{ cursor: "pointer", objectFit: "cover" }}
          {...getRootProps()}
          position="relative"
          overflow="hidden"
        >
          <AddCircleOutlineIcon fontSize="medium" />

          <input {...getInputProps()} />
        </MDBox>
      )}

      <MDBox flex="1">
        <Carousel
          showThumbs={false}
          centerMode
          showIndicators={false}
          centerSlidePercentage={33}
          showStatus={false}
          renderArrowPrev={(clickHandler, hasPrev, label) => (
            <div
              style={{
                opacity: hasPrev ? 1 : 0,
                padding: "5px",
                backgroundColor: "white",
                borderRadius: "50% ",
              }}
            >
              <MDBox
                onClick={clickHandler}
                title={label}
                style={{
                  position: "absolute",
                  zIndex: 2,
                  top: "50%",
                  left: 0,
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <MDBox style={{ padding: "5px", backgroundColor: "white", borderRadius: "50% " }}>
                  <ArrowBackIcon fontSize="medium" />
                </MDBox>
              </MDBox>
            </div>
          )}
          renderArrowNext={(clickHandler) => (
            <div
              style={{
                opacity: hasNext ? 1 : 0,
                padding: "5px",
                backgroundColor: "white",
                borderRadius: "50% ",
              }}
            >
              <MDBox
                onClick={clickHandler}
                style={{
                  position: "absolute",
                  zIndex: 2,
                  top: "50%",
                  right: 0,
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <MDBox style={{ padding: "5px", backgroundColor: "white", borderRadius: "50% " }}>
                  <ArrowForwardIcon fontSize="medium" />
                </MDBox>
              </MDBox>
            </div>
          )}
        >
          {images.map((image: ImageData | IProductImage, index: number) => {
            const src = "file" in image 
              ? (image.preview ?? "") 
              : (image.url ?? getPublicImageUrl(image.phisicalAddress) ?? "");

            return (
              <MDBox
                key={index}
                position="relative"
                borderRadius="lg"
                width="100%"
                height="5rem"
                minHeight="5rem"
                sx={{ cursor: "pointer", objectFit: "contain" }}
                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                  handleSetMainImage(event, image as IProductImage)
                }
                overflow="hidden"
              >
                <img
                  src={src}
                  alt={`Image ${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
                <RemoveIconButton onClick={(event) => removeImage(image as IProductImage, event)}>
                  <CloseIcon sx={{ fontSize: "20px" }} />
                </RemoveIconButton>
              </MDBox>
            );
          })}
        </Carousel>
      </MDBox>
    </MDBox>
  );
});

export default CarouselImages;
